import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, FileText, Download, Lightbulb, Target, MessageCircle } from '../icons';
import { VideoEmbed } from './VideoEmbed';
import { getAssetSignedUrl } from '../../lib/pathways';
import type { StageDetail, PathwayProgress, PathwayTaskResponse, StageStatus } from '../../types/pathway';

interface StageContentProps {
  stage: StageDetail;
  progress: PathwayProgress | undefined;
  taskResponses: PathwayTaskResponse[];
  isLocked: boolean;
  onStartStage: () => void;
  onCompleteStage: () => void;
  onSaveResponse: (taskId: string, response: string) => void;
  onAskCoach?: (prompt: string) => void;
}

export function StageContent({
  stage,
  progress,
  taskResponses,
  isLocked,
  onStartStage,
  onCompleteStage,
  onSaveResponse,
  onAskCoach,
}: StageContentProps) {
  const [localResponses, setLocalResponses] = useState<Record<string, string>>({});
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [urlErrors, setUrlErrors] = useState<Record<string, boolean>>({});
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);

  const status: StageStatus = progress?.status || 'not_started';
  const isCompleted = status === 'completed';

  useEffect(() => {
    const initial: Record<string, string> = {};
    taskResponses.forEach(tr => {
      initial[tr.taskId] = tr.response;
    });
    setLocalResponses(initial);
  }, [taskResponses]);

  useEffect(() => {
    const fileAssets = stage.assets.filter(a => a.storagePath && a.type !== 'video_embed');
    if (fileAssets.length === 0) return;

    const loadUrls = async () => {
      const urls: Record<string, string> = {};
      const errors: Record<string, boolean> = {};
      for (const asset of fileAssets) {
        try {
          urls[asset.id] = await getAssetSignedUrl(asset.storagePath);
        } catch {
          errors[asset.id] = true;
        }
      }
      setSignedUrls(urls);
      setUrlErrors(errors);
    };
    loadUrls();
  }, [stage.assets]);

  const handleSaveResponse = async (taskId: string) => {
    setSavingTaskId(taskId);
    await onSaveResponse(taskId, localResponses[taskId] || '');
    setSavingTaskId(null);
  };

  if (isLocked) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 bg-cream-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <FileText className="w-7 h-7 text-chocolate-400" />
        </div>
        <p className="text-sm text-chocolate-500 mb-1">This stage is locked</p>
        <p className="text-xs text-chocolate-400">Complete the previous stage to unlock this one.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className="freud-heading-xl text-chocolate-900">{stage.title}</h2>
            {stage.objective && (
              <p className="text-sm text-chocolate-500 mt-1">{stage.objective}</p>
            )}
          </div>
          {isCompleted && (
            <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full flex-shrink-0">
              <Check className="w-3.5 h-3.5" />
              Completed
            </span>
          )}
        </div>
        {stage.description && (
          <div className="mt-4 text-sm text-chocolate-700 leading-relaxed whitespace-pre-wrap">
            {stage.description}
          </div>
        )}
      </div>

      {status === 'not_started' && (
        <button
          onClick={onStartStage}
          className="freud-btn-primary text-sm"
        >
          Start This Stage
        </button>
      )}

      {stage.assets.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-chocolate-800">Resources</h3>
          {stage.assets.map(asset => (
            <div key={asset.id}>
              {asset.type === 'video_embed' && asset.url && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-chocolate-600">{asset.title}</p>
                  <VideoEmbed url={asset.url} title={asset.title} />
                </div>
              )}
              {(asset.type === 'pdf' || asset.type === 'worksheet') && (
                urlErrors[asset.id] ? (
                  <div className="flex items-center gap-3 px-4 py-3 bg-red-50 rounded-xl">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <FileText className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-chocolate-800 truncate">{asset.title}</p>
                      <p className="text-xs text-red-500">File unavailable</p>
                    </div>
                  </div>
                ) : (
                  <a
                    href={signedUrls[asset.id] || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-4 py-3 bg-cream-50 hover:bg-cream-100 rounded-xl transition-colors group ${
                      !signedUrls[asset.id] ? 'opacity-60 pointer-events-none' : ''
                    }`}
                  >
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <FileText className="w-4 h-4 text-orange-50" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-chocolate-800 truncate">{asset.title}</p>
                      <p className="text-xs text-chocolate-400 uppercase">{asset.type}</p>
                    </div>
                    <Download className="w-4 h-4 text-chocolate-400 group-hover:text-orange-50 transition-colors" />
                  </a>
                )
              )}
              {asset.type === 'link' && asset.url && (
                <a
                  href={asset.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-cream-50 hover:bg-cream-100 rounded-xl transition-colors group"
                >
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <FileText className="w-4 h-4 text-chocolate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-chocolate-800 truncate">{asset.title}</p>
                    <p className="text-xs text-chocolate-400">Reading</p>
                  </div>
                  <Download className="w-4 h-4 text-chocolate-400 group-hover:text-orange-50 transition-colors rotate-[-45deg]" />
                </a>
              )}
              {asset.type === 'audio' && signedUrls[asset.id] && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-chocolate-600">{asset.title}</p>
                  <audio controls className="w-full" src={signedUrls[asset.id]}>
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
              {asset.type === 'image' && (
                urlErrors[asset.id] ? (
                  <div className="flex items-center gap-3 px-4 py-3 bg-red-50 rounded-xl">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <FileText className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-chocolate-800 truncate">{asset.title}</p>
                      <p className="text-xs text-red-500">Image unavailable</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <a
                      href={signedUrls[asset.id] || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block rounded-xl overflow-hidden border border-cream-200 hover:border-sage-300 transition-colors ${
                        !signedUrls[asset.id] ? 'opacity-60 pointer-events-none' : ''
                      }`}
                    >
                      {signedUrls[asset.id] ? (
                        <img
                          src={signedUrls[asset.id]}
                          alt={asset.title}
                          className="w-full h-auto max-h-[600px] object-contain bg-cream-50"
                        />
                      ) : (
                        <div className="w-full h-40 flex items-center justify-center bg-cream-50">
                          <p className="text-xs text-chocolate-400">Loading image...</p>
                        </div>
                      )}
                    </a>
                    <div className="flex items-center justify-between px-1">
                      <p className="text-xs font-medium text-chocolate-600 truncate">{asset.title}</p>
                      {signedUrls[asset.id] && (
                        <a
                          href={signedUrls[asset.id]}
                          download={asset.title}
                          className="inline-flex items-center gap-1 text-xs text-chocolate-500 hover:text-chocolate-700 transition-colors flex-shrink-0"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </a>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {stage.tasks.length > 0 && (status === 'in_progress' || status === 'completed') && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-chocolate-800">Tasks</h3>
          {stage.tasks.map(task => (
            <div key={task.id} className="freud-card-elevated p-4 space-y-3">
              <div className="flex items-start gap-2">
                <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${
                  task.type === 'reflection'
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {task.type === 'reflection' ? (
                    <span className="inline-flex items-center gap-0.5"><Lightbulb className="w-2.5 h-2.5" /> Reflection</span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5"><Target className="w-2.5 h-2.5" /> Action</span>
                  )}
                </span>
                <p className="text-sm text-chocolate-800">{task.prompt}</p>
              </div>
              <textarea
                value={localResponses[task.id] || ''}
                onChange={e => setLocalResponses(prev => ({ ...prev, [task.id]: e.target.value }))}
                placeholder={task.type === 'reflection' ? 'Write your reflection...' : 'Describe your action plan...'}
                rows={3}
                disabled={isCompleted}
                className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-orange-40 focus:border-orange-50 resize-none disabled:opacity-60 disabled:bg-cream-50"
              />
              {!isCompleted && (
                <button
                  onClick={() => handleSaveResponse(task.id)}
                  disabled={savingTaskId === task.id}
                  className="text-xs px-3 py-1.5 bg-orange-10 text-orange-70 hover:bg-orange-40 hover:text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {savingTaskId === task.id ? 'Saving...' : 'Save Response'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {onAskCoach && (status === 'in_progress' || status === 'completed') && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-chocolate-800">AI Coach</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onAskCoach(`Help me understand the concepts in "${stage.title}"`)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-orange-10 text-orange-70 hover:bg-orange-40 hover:text-white rounded-xl transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Ask about this module
            </button>
            <button
              onClick={() => onAskCoach(`Help me apply the concepts from "${stage.title}" to my assessment results`)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-orange-10 text-orange-70 hover:bg-orange-40 hover:text-white rounded-xl transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Apply to my results
            </button>
            <button
              onClick={() => onAskCoach(`Create an action plan for this week based on "${stage.title}" and its objective: ${stage.objective}`)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-orange-10 text-orange-70 hover:bg-orange-40 hover:text-white rounded-xl transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Weekly action plan
            </button>
          </div>
        </div>
      )}

      {status === 'in_progress' && !isCompleted && (
        <div className="pt-4 border-t border-cream-200">
          <button
            onClick={onCompleteStage}
            className="freud-btn-primary text-sm inline-flex items-center gap-1.5"
          >
            <Check className="w-4 h-4" />
            Mark Stage as Complete
          </button>
        </div>
      )}
    </motion.div>
  );
}
